import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { get, post, put } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { toast } from '@/store/toastStore';

/**
 * 어드민 — 이벤트 작성/수정 통합 페이지.
 *  - `/admin/events/new`         → 새 이벤트 작성 (POST /events 단독)
 *  - `/admin/events/:id/edit`    → 기존 이벤트 수정 (PUT /events/:id)
 *
 * 핵심 필드만 노출 (tier_rewards 등 복합 보상은 WritePage 이벤트 폼 추출 작업 시 도입 예정).
 * post 연결된 이벤트(events.post_id 존재) 도 events 만 갱신 — 일반 사용자가 보는 EventDetail
 * 페이지는 events.description 기반이라 본문 동기화 영향 없음. */

interface EventEditForm {
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  isPerpetual: boolean;
  instantPoint: number;
  winnerPoint: number;
  winnerCount: number;
  winnerCriteria: string;
  actionType: string;
  actionUrl: string;
  howToJoin: string;
  caution: string;
  announceDate: string;
}

export function AdminEventEditPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = id ? Number(id) : null;
  const isNew = !eventId; // 라우트가 /admin/events/new 면 id 없음
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['events', eventId ?? -1, 'admin-edit'],
    queryFn: () => get<Record<string, unknown>>(`/events/${eventId}`),
    enabled: !isNew && Number.isFinite(eventId) && (eventId as number) > 0,
    staleTime: 0,
  });

  // 신규 작성 기본값 — 오늘 시작 / 일주일 후 종료
  const defaultForm = (): EventEditForm => {
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 86400 * 1000);
    const toLocal = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    return {
      title: '',
      description: '',
      startsAt: toLocal(now),
      endsAt: toLocal(week),
      isPerpetual: false,
      instantPoint: 0,
      winnerPoint: 0,
      winnerCount: 0,
      winnerCriteria: 'admin_pick',
      actionType: 'general',
      actionUrl: '',
      howToJoin: '',
      caution: '',
      announceDate: '',
    };
  };

  const [form, setForm] = useState<EventEditForm | null>(isNew ? defaultForm() : null);

  // 수정 모드 — 서버 응답 hydrate (한 번만)
  useEffect(() => {
    if (isNew || !data || form) return;
    const toLocal = (iso: string | null | undefined): string => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const d = data as Record<string, unknown>;
    setForm({
      title: String(d.title ?? ''),
      description: String(d.description ?? ''),
      startsAt: toLocal(d.starts_at as string),
      endsAt: toLocal(d.ends_at as string),
      isPerpetual: Boolean(d.is_perpetual),
      instantPoint: Number(d.instant_point ?? 0),
      winnerPoint: Number(d.winner_point ?? 0),
      winnerCount: Number(d.winner_count ?? 0),
      winnerCriteria: String(d.winner_criteria ?? 'admin_pick'),
      actionType: String(d.action_type ?? 'general'),
      actionUrl: String(d.action_url ?? ''),
      howToJoin: String(d.how_to_join ?? ''),
      caution: String(d.caution ?? ''),
      announceDate: String(d.announce_date ?? '').slice(0, 10),
    });
  }, [data, form, isNew]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const toIso = (local: string): string | undefined =>
        local ? new Date(local).toISOString() : undefined;
      // 공통 body — POST /events 스키마 (createEventSchema) 와 PUT (updateEventSchema) 양쪽 호환.
      // POST 는 startsAt/endsAt 필수, PUT 은 optional. 신규에서는 항상 채워 보냄.
      const body: Record<string, unknown> = {
        title: form.title,
        description: form.description,
        startsAt: toIso(form.startsAt),
        endsAt: toIso(form.endsAt),
        instantPoint: form.instantPoint,
        winnerPoint: form.winnerPoint,
        winnerCount: form.winnerCount,
        actionType: form.actionType,
        howToJoin: form.howToJoin || null,
        caution: form.caution || null,
        announceDate: form.announceDate || null,
      };
      // PUT 만 받는 필드 — POST 스키마엔 없지만 보내도 무해 (zod 가 무시)
      if (!isNew) {
        body.isPerpetual = form.isPerpetual;
        body.winnerCriteria = form.winnerCriteria;
        body.actionUrl = form.actionUrl || null;
      }
      if (isNew) {
        await post(`/events`, body);
      } else {
        await put(`/events/${eventId}`, body);
      }
    },
    onSuccess: () => {
      toast.success(isNew ? '이벤트를 생성했어요.' : '이벤트가 수정되었어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'events'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      if (!isNew && eventId) {
        qc.invalidateQueries({ queryKey: ['events', eventId] });
      }
      navigate('/admin/events');
    },
    onError: () => toast.error('저장에 실패했어요.'),
  });

  return (
    <div className="w-full max-w-3xl">
      {/* 상단 — 뒤로가기 + 제목 */}
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/admin/events')}
          className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1.5 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          목록으로
        </button>
        <h1 className="text-2xl font-extrabold">
          {isNew ? '새 이벤트 작성' : '이벤트 수정'}
        </h1>
      </div>
      <p className="mb-6 text-xs text-[var(--color-text-sub)]">
        {isNew
          ? '단독 이벤트로 생성됩니다 (게시글은 만들지 않음).'
          : '핵심 필드만 편집 가능 — tier_rewards 등 복잡 보상은 새로 작성해주세요.'}
      </p>

      {(!isNew && isLoading) || !form ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <Input
            placeholder="제목"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <textarea
            placeholder="설명 (HTML/평문 모두 허용)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="min-h-[200px] w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm"
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-[var(--color-text-sub)]">
              시작일시
              <Input
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              />
            </label>
            <label className="text-xs text-[var(--color-text-sub)]">
              종료일시
              <Input
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                disabled={form.isPerpetual}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isPerpetual}
              onChange={(e) => setForm({ ...form, isPerpetual: e.target.checked })}
            />
            상시 이벤트 (마감 없음)
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-[var(--color-text-sub)]">
              참여 즉시 포인트
              <Input
                type="number"
                min={0}
                value={form.instantPoint}
                onChange={(e) => setForm({ ...form, instantPoint: Number(e.target.value) })}
              />
            </label>
            <label className="text-xs text-[var(--color-text-sub)]">
              당첨 포인트
              <Input
                type="number"
                min={0}
                value={form.winnerPoint}
                onChange={(e) => setForm({ ...form, winnerPoint: Number(e.target.value) })}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-[var(--color-text-sub)]">
              당첨 기준
              <select
                value={form.winnerCriteria}
                onChange={(e) => setForm({ ...form, winnerCriteria: e.target.value })}
                className="mt-1 block h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-sm"
              >
                <option value="admin_pick">관리자 직접 선정</option>
                <option value="random">무작위 추첨</option>
                <option value="first_come">선착순</option>
                <option value="top_likes">추천순 상위</option>
                <option value="top_1">1등</option>
                <option value="top_3">상위 3등</option>
                <option value="top_10">상위 10등</option>
                <option value="none">당첨자 없음</option>
              </select>
            </label>
            <label className="text-xs text-[var(--color-text-sub)]">
              당첨자 수
              <Input
                type="number"
                min={0}
                value={form.winnerCount}
                onChange={(e) => setForm({ ...form, winnerCount: Number(e.target.value) })}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-[var(--color-text-sub)]">
              참여 버튼 동작
              <select
                value={form.actionType}
                onChange={(e) => setForm({ ...form, actionType: e.target.value })}
                className="mt-1 block h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-sm"
              >
                <option value="none">버튼 숨김</option>
                <option value="general">일반 참여</option>
                <option value="random">무작위 추첨</option>
                <option value="checkin">출석체크</option>
                <option value="write_post">글쓰기 유도</option>
                <option value="virtual">모의투자</option>
                <option value="url">외부 URL 이동</option>
              </select>
            </label>
            <label className="text-xs text-[var(--color-text-sub)]">
              액션 URL (url 타입일 때)
              <Input
                placeholder="https://..."
                value={form.actionUrl}
                onChange={(e) => setForm({ ...form, actionUrl: e.target.value })}
                disabled={form.actionType !== 'url'}
              />
            </label>
          </div>

          <Input
            placeholder="참여 방법"
            value={form.howToJoin}
            onChange={(e) => setForm({ ...form, howToJoin: e.target.value })}
          />
          <Input
            placeholder="주의사항"
            value={form.caution}
            onChange={(e) => setForm({ ...form, caution: e.target.value })}
          />
          <label className="text-xs text-[var(--color-text-sub)]">
            발표일
            <Input
              type="date"
              value={form.announceDate}
              onChange={(e) => setForm({ ...form, announceDate: e.target.value })}
            />
          </label>

          <div className="flex gap-2 pt-2">
            <Button variant="ghost" fullWidth onClick={() => navigate('/admin/events')}>
              취소
            </Button>
            <Button
              fullWidth
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate()}
              disabled={!form.title}
            >
              저장
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
